import React, { useCallback } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { applicationPath, isOpenInNewTabClick } from "@/components/borrower/openApplication";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { analysisListSearch, type AnalysisListFilter } from "./analysisListLink";

/** Where "Show in application" goes: the AI Analysis list of one application. */
export interface ShowInApplicationTarget {
  applicationId: number;
  /** The analysis run the rows come from. */
  run?: number | null;
  /** A month's credits, a set of rows by override key, or a position's transactions. */
  filter?: AnalysisListFilter | null;
  /** Override keys of the rows to emphasize; the first is scrolled into view. */
  highlight?: string[];
}

/** The application page's address that opens its AI Analysis list as `target` asks. */
export function showInApplicationHref(target: ShowInApplicationTarget): string {
  const search = analysisListSearch({
    run: target.run ?? null,
    filter: target.filter ?? null,
    highlight: target.highlight ?? [],
  });
  return `${applicationPath(target.applicationId)}?${search}`;
}

/**
 * Opens the AI Analysis list as `target` asks. On that application's page it applies in place
 * (the page switches tab and the list applies the filter); anywhere else (a KYC page, the
 * standalone assistant) it navigates to the application page. Either way it is a history entry,
 * so Back returns to the view before; opening the address already shown replaces it instead.
 */
export function useShowInApplication(): (target: ShowInApplicationTarget) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (target) => {
      const href = showInApplicationHref(target);
      const [path, query] = href.split("?");
      if (location.pathname === path) {
        navigate(
          { search: `?${query}` },
          { preventScrollReset: true, replace: location.search === `?${query}` }
        );
        return;
      }
      navigate(href);
    },
    [navigate, location.pathname, location.search]
  );
}

interface ShowInApplicationLinkProps {
  target: ShowInApplicationTarget;
  className?: string;
  children?: React.ReactNode;
}

/** "Show in application" for card footers: a real link (new tab on Ctrl/Cmd-click), small button look. */
export function ShowInApplicationLink({ target, className, children }: ShowInApplicationLinkProps) {
  const show = useShowInApplication();
  return (
    <Link
      to={showInApplicationHref(target)}
      onClick={(event) => {
        if (isOpenInNewTabClick(event) || event.shiftKey || event.altKey) return;
        event.preventDefault();
        show(target);
      }}
      className={cn(
        buttonVariants({ variant: "outline", size: "sm" }),
        "h-7 gap-1.5 px-2.5 text-xs",
        className
      )}
    >
      <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
      {children ?? "Show in application"}
    </Link>
  );
}

export default ShowInApplicationLink;
